import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Eye, EyeOff, Check, X, Pencil, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useCategories } from "../../context/CategoryContext";

const inputCls =
  "border border-brand-line bg-white px-3 py-2 text-sm outline-none focus:border-brand-ink rounded-md";

const AdminCategories = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id, name_az, name_en}
  const [adding, setAdding] = useState(null); // parent_id və ya "root"
  const [draft, setDraft] = useState({ name_az: "", name_en: "" });
  const { reload: reloadPublic } = useCategories();

  const load = useCallback(
    () =>
      api
        .get("/admin/categories")
        .then(({ data }) => setGroups(data.groups || []))
        .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
        .finally(() => setLoading(false)),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  // Saytın menyusu da dərhal yenilənsin.
  const refresh = async () => {
    await load();
    reloadPublic();
  };

  const toggleActive = async (node) => {
    try {
      await api.put(`/admin/categories/${node.id}`, { is_active: !node.is_active });
      toast.success(node.is_active ? `${node.name_az} gizlədildi` : `${node.name_az} göstərilir`);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/categories/${editing.id}`, {
        name_az: editing.name_az,
        name_en: editing.name_en,
      });
      setEditing(null);
      toast.success("Ad dəyişdirildi");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const create = async (parentId) => {
    if (!draft.name_az.trim()) {
      toast.error("Ad boş ola bilməz");
      return;
    }
    try {
      await api.post("/admin/categories", {
        name_az: draft.name_az,
        name_en: draft.name_en || draft.name_az,
        parent_id: parentId,
        sort_order: 999,
      });
      setAdding(null);
      setDraft({ name_az: "", name_en: "" });
      toast.success("Əlavə edildi");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (node) => {
    if (!window.confirm(`"${node.name_az}" silinsin? Bu geri qaytarıla bilməz.`)) return;
    try {
      await api.delete(`/admin/categories/${node.id}`);
      toast.success("Silindi");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const move = async (node, siblings, dir) => {
    const i = siblings.findIndex((s) => s.id === node.id);
    const j = i + dir;
    if (j < 0 || j >= siblings.length) return;
    try {
      await Promise.all([
        api.put(`/admin/categories/${node.id}`, { sort_order: j }),
        api.put(`/admin/categories/${siblings[j].id}`, { sort_order: i }),
      ]);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const NameCell = ({ node, bold }) =>
    editing?.id === node.id ? (
      <div className="flex flex-wrap items-center gap-2">
        <input
          className={inputCls}
          value={editing.name_az}
          onChange={(e) => setEditing({ ...editing, name_az: e.target.value })}
          placeholder="Azərbaycanca"
          autoFocus
        />
        <input
          className={inputCls}
          value={editing.name_en}
          onChange={(e) => setEditing({ ...editing, name_en: e.target.value })}
          placeholder="English"
        />
        <button onClick={saveEdit} className="text-green-700 p-1" title="Yadda saxla">
          <Check size={18} />
        </button>
        <button onClick={() => setEditing(null)} className="text-brand-muted p-1" title="Ləğv et">
          <X size={18} />
        </button>
      </div>
    ) : (
      <span className={`${bold ? "font-serif text-xl" : "text-sm"} ${node.is_active ? "" : "text-brand-muted line-through"}`}>
        {node.name_az}
        <span className="text-brand-muted text-xs ml-2">{node.name_en}</span>
        {node.product_count > 0 && (
          <span className="text-brand-muted text-xs ml-3">· {node.product_count} məhsul</span>
        )}
      </span>
    );

  const Actions = ({ node, siblings }) => (
    <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
      <button onClick={() => move(node, siblings, -1)} className="p-2 text-brand-muted hover:text-brand-ink" title="Yuxarı">
        <ChevronUp size={16} />
      </button>
      <button onClick={() => move(node, siblings, 1)} className="p-2 text-brand-muted hover:text-brand-ink" title="Aşağı">
        <ChevronDown size={16} />
      </button>
      <button
        onClick={() => setEditing({ id: node.id, name_az: node.name_az, name_en: node.name_en })}
        className="p-2 text-brand-muted hover:text-brand-ink"
        title="Adını dəyiş"
      >
        <Pencil size={16} />
      </button>
      <button
        onClick={() => toggleActive(node)}
        className={`p-2 ${node.is_active ? "text-brand-ink" : "text-brand-muted"}`}
        title={node.is_active ? "Saytda gizlət" : "Saytda göstər"}
        data-testid={`toggle-${node.key}`}
      >
        {node.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
      </button>
      <button onClick={() => remove(node)} className="p-2 text-brand-muted hover:text-red-600" title="Sil">
        <Trash2 size={16} />
      </button>
    </div>
  );

  const AddRow = ({ parentId, onCancel }) => (
    <div className="flex flex-wrap items-center gap-2 py-3">
      <input
        className={inputCls}
        placeholder="Ad (AZ) *"
        value={draft.name_az}
        onChange={(e) => setDraft({ ...draft, name_az: e.target.value })}
        autoFocus
      />
      <input
        className={inputCls}
        placeholder="Name (EN)"
        value={draft.name_en}
        onChange={(e) => setDraft({ ...draft, name_en: e.target.value })}
      />
      <button
        onClick={() => create(parentId)}
        className="bg-brand-ink text-white px-4 py-2 text-xs tracking-widest uppercase rounded-md"
      >
        Əlavə et
      </button>
      <button onClick={onCancel} className="text-brand-muted text-sm px-2">
        Ləğv
      </button>
    </div>
  );

  if (loading) return <p className="text-brand-muted">Yüklənir...</p>;

  return (
    <div data-testid="admin-categories">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
        <h1 className="font-serif font-light text-3xl sm:text-4xl tracking-tight">Kateqoriyalar</h1>
        <button
          onClick={() => { setAdding("root"); setDraft({ name_az: "", name_en: "" }); }}
          className="flex items-center gap-2 bg-brand-ink text-white px-5 py-2.5 text-xs tracking-widest uppercase rounded-md"
          data-testid="add-group"
        >
          <Plus size={16} /> Yeni bölmə
        </button>
      </div>
      <p className="text-sm text-brand-muted mb-8 max-w-2xl">
        <strong>Göz</strong> düyməsi kateqoriyanı saytda gizlədir — məhsullar silinmir, sadəcə
        müştəriyə görünmür. Bölməni gizlətsəniz, onun bütün alt kateqoriyaları da gizlənir.
        Məhsulu olan kateqoriya silinə bilməz — əvəzinə gizlədin.
      </p>

      {adding === "root" && <AddRow parentId={null} onCancel={() => setAdding(null)} />}

      <div className="space-y-6">
        {groups.map((g) => (
          <div key={g.id} className="bg-white border border-brand-line rounded-md">
            <div className={`flex items-center justify-between gap-2 sm:gap-4 p-4 sm:p-5 border-b border-brand-line ${g.is_active ? "" : "bg-brand-bg2"}`}>
              <NameCell node={g} bold />
              <Actions node={g} siblings={groups} />
            </div>

            <div className="divide-y divide-brand-line">
              {(g.children || []).map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 sm:gap-4 px-4 sm:px-5 py-3">
                  <NameCell node={c} />
                  <Actions node={c} siblings={g.children} />
                </div>
              ))}
            </div>

            <div className="px-5 pb-2">
              {adding === g.id ? (
                <AddRow parentId={g.id} onCancel={() => setAdding(null)} />
              ) : (
                <button
                  onClick={() => { setAdding(g.id); setDraft({ name_az: "", name_en: "" }); }}
                  className="flex items-center gap-2 text-sm text-brand-muted hover:text-brand-ink py-3"
                  data-testid={`add-child-${g.key}`}
                >
                  <Plus size={15} /> {g.name_az} bölməsinə alt kateqoriya əlavə et
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminCategories;
